import { Address } from 'react-daum-postcode';
import { RcFile } from 'antd/es/upload';
import { ReactComponent as OfficeIcon } from '@assets/images/app/icon-building.svg';
import { ReactComponent as DownloadIcon } from '@assets/images/app/icon-download.svg';
import { ReactComponent as HomeIcon } from '@assets/images/app/icon-home.svg';
import { ReactComponent as ExperienceIcon } from '@assets/images/app/icon-light.svg';
import { ReactComponent as TicketIcon } from '@assets/images/app/icon-ticket.svg';
import { ReactComponent as DeliveryIcon } from '@assets/images/app/icon-truck.svg';
import { ReactComponent as OnlineIcon } from '@assets/images/app/icon-untact.svg';
import { Membership } from './membership';
import { PaymentStatementSchedule } from './paymentStatement';
import { IntelligenceType } from './programReport';

export interface AddressInfo extends Address {
  address2?: string;
}

export interface ClassBadge {
  type: string;
  isActive: boolean;
  value: string;
}

export interface AvailableDistrict {
  state: string;
  city: string;
}

export interface SpecificAddress {
  postcode: string;
  postcode1: string;
  postcode2: string;
  postcodeSeq: string;
  zonecode: string;
  address: string;
  addressEnglish: string;
  addressType: string;
  bcode: string;
  bname: string;
  bnameEnglish: string;
  bname1: string;
  bname1English: string;
  bname2: string;
  bname2English: string;
  sido: string;
  sidoEnglish: string;
  sigungu: string;
  sigunguEnglish: string;
  sigunguCode: string;
  userLanguageType: string;
  query: string;
  buildingName: string;
  buildingCode: string;
  apartment: string;
  jibunAddress: string;
  jibunAddressEnglish: string;
  roadAddress: string;
  roadAddressEnglish: string;
  autoRoadAddress: string;
  autoRoadAddressEnglish: string;
  autoJibunAddress: string;
  autoJibunAddressEnglish: string;
  userSelectedType: string;
  noSelected: string;
  hname: string;
  roadnameCode: string;
  roadname: string;
  roadnameEnglish: string;
  address2: string;
}

export interface ProductLocationData {
  specificAddress?: SpecificAddress | AddressInfo;
  availableDistricts?: AvailableDistrict[];
}

export interface BottomTextBanner {
  show?: boolean;
  iconTemp?: string;
  icon?: string;
  highlightText?: string;
  normalText?: string;
}

export interface Concept {
  conceptTitle?: string;
  conceptData?: string;
  images?: string[];
  imagesTemp?: string[];
}

export interface CommerceFixedOptionItem {
  groupName?: string;
  title?: string;
  fee?: number;
  essential?: boolean;
  inventory?: number;
}

export interface ProductOption {
  _id?: string;
  productId?: string;
  step: string[];
  type?: 'direct' | 'sequential';
  fee?: number;
  inventory?: number;
  index?: number;
  yyyymmdd?: string;
  date?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

export enum ProductDetailType {
  TEMPLATE = 'template',
  IMAGES = 'images',
  HTML = 'html',
}
export interface ProductDetails {
  disableDetailImage?: boolean;
  disableClassIntroduction?: boolean;
  disableRecommendText?: boolean;
  disableConcepts?: boolean;
  disablePreparationText?: boolean;
  introductions?: { title?: string; description?: string }[];
  concepts?: Concept[];
  preparationText?: string;
  recommendText?: string;
  youtubeId?: string;
  videoUrl?: string;
  detailImage?: string;
  detailImageTemp?: string;
}

export interface ProductDetailsEditForm {
  disableDetailImage?: boolean;
  disableClassIntroduction?: boolean;
  disableRecommendText?: boolean;
  disableConcepts?: boolean;
  disablePreparationText?: boolean;
  introductions?: { title?: string; description?: string }[];
  concepts?: Concept[];
  recommends?: string[];
  preparations?: string[];
  youtubeId?: string;
  videoUrl?: string;
  detailImage?: string;
  detailImageTemp?: string;
}

export interface DiscountInfo {
  discountStartDate?: Date;
  discountEndDate?: Date;
  discountedFee?: number;
}

export enum ProductActivityType {
  HOME = 'home',
  STUDIO = 'studio',
  EXPERIENTIAL = 'experiential',
  ONLINE = 'online',
  DIGITAL = 'digital',
  DELIVERY = 'delivery',
  TICKET = 'ticket',
}

export enum ProductSaleType {
  CLASS = 'class',
  COMMERCE = 'commerce',
  // PRE_PAID = 'pre_paid',
}

export class CustomerServiceInfo {
  mainCustomerService?: 'url' | 'phone';
  url?: string;
  phone?: string;
}

export enum LegacyProductStatus {
  ALL = 'all',
  OPEN = 'open',
  CLOSE = 'close',
  HIDE = 'hide',
  DENIED = 'denied',
  OPEN_REQUEST = 'open_request',
  EDIT = 'edit',
  PRE_OPEN = 'pre_open',
}
export enum ProductStatus {
  OPEN = 'open',
  CLOSE = 'close',
  PRIVATE = 'private',
}
export enum ProductApproveStatus {
  ACCEPTED = 'accepted',
  REQUESTED = 'requested',
  DENIED = 'denied',
  NOT_READY = 'not_ready',
  READY = 'ready',
}

export enum LinkedSalesChannelStatus {
  REQUESTED = 'requested',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  OPEN = 'open',
  CLOSE = 'close',
}

export interface ProductModel extends Product {
  classIntroductionImageTemp?: string;
  thumbnailImageTemp?: string;
  detailImagesTemp?: string[];
  detailBodyImagesTemp?: string[];
}

export interface ProductEditModel extends Product {
  classIntroductionImageTemp?: string;
  thumbnailImageTemp?: string;
  detailImagesTemp?: string[];
  detailBodyImagesTemp?: string[];
}

export interface PreferredScheduleConfig {
  startTime: string;
  endTime: string;
  intervalMinutes: 30 | 60;
}

export interface QuestionOption {
  question: string;
  questionType: string;
  preferredSchedule?: PreferredScheduleConfig;
}

export class SalesChannelInfo {
  naverStore?: {
    channelProductNo: string;
    originProductNo: string;
    requestedAt?: Date;
    openedAt?: Date;
    status?: string; // requested, accepted, rejected, open, close
    rejectReason?: string;
  };
  kakaoReservation?: {
    productUrl?: string;
    requestedAt?: Date;
    openedAt?: Date;
    status?: string;
    rejectReason?: string;
  };
  ezwel?: {
    gdsCd?: string;
    status?: string;
    exclusive?: boolean;
    purchasePrice?: number;
    sellPrice?: number;
    brandCd?: string;
    mngStdCtgrCd?: string;
    rejectReason?: string;
    requestedAt?: Date;
    openedAt?: Date;
    lastSyncedAt?: Date;
    gdsSellStCd?: string;
    gdsApvStCd?: string;
  };
}

export interface LabelInfo {
  name: string;
  source: string; // admin, partner
  createdAt: Date;
}

export interface Product {
  _id: string;
  sellerName: string;
  sellerId: string;
  sellerProfileImage?: string;
  sellerIntroduction?: string;
  title: string;
  saleType: ProductSaleType;
  activityType?: string;
  favoritedCount?: number;
  thumbnailImage: string;
  closeText?: string;
  discount?: string;
  originFee: number;
  fee: number;
  classDuration?: number;
  durations?: { hour?: number; minute?: number; duration?: number }[];
  totalNumber: number;
  shippingProduct?: boolean;
  showRegularTime?: boolean;
  regularTime?: number;
  labels?: LabelInfo[];
  tags: string[];
  badges: ClassBadge[];
  location: ProductLocationData;
  detailType: ProductDetailType;
  detailImages: string[];
  detailImagesTemp?: string[];
  detailHtml: string;
  details: ProductDetails;
  detailBodyImages?: string[];
  classIntroductionImage?: string | null;
  refundPolicyText?: string;
  shippingPolicyText?: string;
  classTermsText?: string;
  refundPolicies?: string[];
  applyGroupDiscount?: boolean;
  ageMin: number;
  ageMax: number;
  studentsMin?: number;
  studentsMax?: number;
  categories: string[];
  rootCategories: string[];
  bottomTextBanner?: BottomTextBanner;
  display?: boolean;
  soldout?: boolean;
  isClosed?: boolean;
  isLastChance?: boolean;
  totalInventory?: number;
  shippingFee?: number;
  memoRequired?: boolean;
  memoComment?: string;
  sellerComment?: string;
  sellerShortComment?: string;
  status?: ProductStatus | LegacyProductStatus;
  approveStatus?: ProductApproveStatus;
  optionType?: string; // direct / sequential / sequential_with_direct
  purchaseQuantityOptions?: CommerceFixedOptionItem[];
  productOptions?: ProductOption[];
  optionLabels?: string[];
  optionDescription?: string;
  meta: any;
  priority?: number;
  approvedAt: Date;
  adminClosedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  applyPeriodDiscount?: boolean;
  periodDiscountExcludeChannels?: string[]; // naver, kakao, benepia, app
  discountInfo?: DiscountInfo;
  shippingFeeExtra?: number;
  shippingComment?: string;
  customerServiceInfo?: CustomerServiceInfo;
  questionOptions?: QuestionOption[];
  deniedReason?: string;
  memberships?: Membership[];
  autoCloseEnabled?: boolean;
  autoCloseDuration?: number;
  linkedSalesChannel?: SalesChannelInfo;
  programReportAvailable?: boolean;
  programReportItems?: IntelligenceType[];
  statistics: any;
  statistics1week?: any;
  statistics3month?: any;
  appOnly?: boolean;
  impressions?: { value: number; unit: string };
  commissionRateId?: string;
  viewCount?: number;
  firstApprovedAt?: Date;
  forceOptionModal?: boolean;
  gpsLocation?: { type: string; coordinates: number[] };
  coupangProduct?: {
    title: string;
    products: {
      linkUrl: string;
      thumbnailImage: string;
      thunbnailAltText: string;
      width: number;
    }[];
  };
  baseRecommendationScore?: number;
  groupingPolicy?: import('./grouping-policy').ProductGroupingPolicyOverride;
}

export enum StockStatus {
  SOLDOUT = 'soldout',
  IN_STUCK = 'in_stock',
  SOLDOUT_STATUS = 'soldout_status',
  NO_SOLDOUT_STATUS = 'no_soldout_status',
}

export interface ProductSearchQuery {
  original?: boolean;
  keyword?: string;
  saleType?: string;
  sort?: string;
  sortType?: string; // createdAt_desc | updatedAt_desc
  activityType?: string;
  category?: string;
  age?: string;
  region?: string;
  status?: ProductStatus | '';
  approveStatus?: ProductApproveStatus | '';
  displayStatus?: string;
  stockStatus?: StockStatus;
  page?: number;
  size?: number;
}

export interface ProductDefaultInfo {
  activityType?: string;
  selectedCategories?: string[];
  detailImageFiles?: { [key: string]: RcFile };
  detailImagesTemp?: string[];
  detailImages?: string[];
  thumbnailImageFile?: { [key: string]: RcFile };
  thumbnailImageTemp?: string;
  thumbnailImage?: string;
  productName?: string;
  ageMin?: number;
  ageMax?: number;
  duration?: { hour?: number; minute?: number; duration?: number };
  durations?: { hour?: number; minute?: number; duration?: number }[];
  studentsMin?: number;
  studentsMax?: number;
  location?: ProductLocationData;
  programReportAvailable?: boolean;
  programReportItems?: IntelligenceType[];
}

export interface ProductDetailInfo {
  disableDetailImage?: boolean;
  disableClassIntroduction?: boolean;
  disableRecommendText?: boolean;
  disableConcepts?: boolean;
  disablePreparationText?: boolean;
  tags?: string[];
  classIntroductionImageFile?: { [key: string]: RcFile };
  classIntroductionImageTemp?: string[];
  classIntroductionImage?: string[];
  templateDetailImageFile?: { [key: string]: RcFile };
  conceptImageFiles?: { [key: string]: RcFile };
  detailBodyImageFiles?: { [key: string]: RcFile };
  detailBodyImagesTemp?: string[];
  detailBodyImages?: string[];
  details?: ProductDetailsEditForm;
  detailType?: ProductDetailType;
  sellerComment?: string;
  questionOptions?: QuestionOption[];
}

export interface ProductPriceInfo {
  fee?: number;
  originFee?: number;
  shippingProduct?: boolean;
  shippingFee?: number;
  shippingFeeExtra?: number;
  shippingComment?: string;
  shippingPolicyText?: string;
  refundPolicyText?: string;
  applyGroupDiscount?: boolean;
  applyPeriodDiscount?: boolean;
  periodDiscountExcludeChannels?: string[]; // naver, kakao, benepia, app
  discountInfo?: DiscountInfo;
  autoCloseEnabled?: boolean;
  autoCloseDuration?: number;
}

export const PRODUCT_ACTIVITY_TYPE_TEXT: { [key: string]: { title: string; description: string; Icon: any } } = {
  [ProductActivityType.STUDIO]: {
    title: '스튜디오',
    description: '튜터님이 운영하는 공간으로 방문하는 수업입니다.',
    Icon: OfficeIcon,
  },
  [ProductActivityType.EXPERIENTIAL]: {
    title: '체험수업',
    description: '',
    Icon: ExperienceIcon,
  },
  [ProductActivityType.HOME]: {
    title: '가정방문',
    description: '튜터님이 집으로 오시는 방문수업입니다.',
    Icon: HomeIcon,
  },
  [ProductActivityType.ONLINE]: {
    title: '비대면',
    description: '튜터님과 협의한 시간에 실시간 비대면으로 진행되는 수업입니다.',
    Icon: OnlineIcon,
  },
  [ProductActivityType.DIGITAL]: {
    title: '디지털상품',
    description: 'PDF 등의 디지털 파일을 구매하는 상품입니다.',
    Icon: DownloadIcon,
  },
  [ProductActivityType.TICKET]: {
    title: '티켓상품',
    description: '티켓 상품입니다.',
    Icon: TicketIcon,
  },
  [ProductActivityType.DELIVERY]: {
    title: '배송상품',
    description: '기입하신 주소지로 배송되는 상품입니다.',
    Icon: DeliveryIcon,
  },
};

export interface PaymentStatementScheduleInfo {
  name?: string;
  birthYear?: string;
  friends?: string;
  [key: string]: any;
}

export interface DerivedScheduleData {
  schedules: PaymentStatementSchedule[];
  additionalPurchaseOptions: { name: string; count: number }[];
}

export interface QuestionOptionEditModel extends QuestionOption {
  answer?: string;
}
